import { memo, useCallback } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import Calendar from "react-calendar";

import { CalendarDay } from "features/CalendarDay";
import type { TCalendarDays } from "features/CalendarMonthOverview/timetableDays";

import { formatDate } from "utils/calendarDateFormatting";
import { useDateNames } from "utils/hooks/useDateNames";
import { isDateInPastWeek } from "utils/validateWeekRange";

import LogoUrl from "images/logos/tonatrenujes.svg?url";
import { useAppSelector } from "store/hooks";
import { getGrade, selectAdmissionsDeadline } from "store/slices/user";

interface Props {
  date: Date;
  tests: Set<string>;
  timetableData: TCalendarDays;
  /** Dnešní den jako `YYYY-MM-DD`. Zvenku, aby se o půlnoci prolomilo `memo` a přepočítaly dlaždice. */
  todayKey: string;
}

const isClickDisabled = ({ date }: { date: Date }) => isDateInPastWeek(date);

const CalendarMonth: FC<Props> = ({ date, tests, timetableData, todayKey }) => {
  const { t } = useTranslation(["calendar"]);
  const admissionsDeadline = useAppSelector(selectAdmissionsDeadline);
  const grade = useAppSelector(getGrade);
  const { monthNames } = useDateNames();

  // AIDEV-NOTE: Callback je stabilní, dokud se nezmění data měsíce nebo den (`todayKey`), takže
  // react-calendar nevyrábí dlaždice znovu kvůli novému `tileContent`. Dnešek se NEčte z hodin při renderu:
  // `memo` + stabilní callback by o půlnoci nic nepřepočítaly a stránka otevřená přes půlnoc by dál
  // zvýrazňovala včerejšek a ukazovala plánovanou tečku u minulého dne. Změna `todayKey` prolomí `memo` i
  // callback. Texty se předpočítávají tady, ne v CalendarDay: štítek podle ročníku a minuty podle českého
  // skloňování. [TD-476]
  const createCalendarDay = useCallback(
    ({ date: tileDate }: { date: Date }) => {
      const dateString = formatDate(tileDate);
      const dayData = timetableData[dateString];

      const isToday = dateString === todayKey;
      const isFuture = dateString > todayKey;
      const withTest = tests.has(dateString);
      const plannedExerciseDuration = dayData?.duration ?? 0;
      const isFinished = dayData?.sessionFinished ?? false;
      const isPlannedExercise = (isFuture || isToday) && plannedExerciseDuration > 0 && !isFinished;
      const isDeadline = dateString === admissionsDeadline;
      const status = dayData?.status ?? null;

      const labelText = isDeadline
        ? t(grade === "maturant" ? "dayOfMaturita" : "dayOfAdmission", { ns: "calendar" })
        : withTest
          ? t("dayOfTest", { ns: "calendar" })
          : null;

      return (
        <CalendarDay
          dayOfMonth={tileDate.getDate()}
          isToday={isToday}
          isDeadline={isDeadline}
          withTest={withTest}
          labelText={labelText}
          showPlannedDot={isPlannedExercise && status === null}
          durationText={
            plannedExerciseDuration > 0
              ? `${plannedExerciseDuration} ${t("minute", { ns: "common", count: plannedExerciseDuration })}`
              : null
          }
          status={status}
        />
      );
    },
    [timetableData, tests, admissionsDeadline, grade, todayKey, t],
  );

  return (
    <div className={"calendar"}>
      <img src={LogoUrl} alt="" className={"calendar__print-logo d-none d-print-block"} style={{ height: "44px" }} />

      <h2 className={"calendar-header h4 text-center"}>{monthNames[date.getMonth()]}</h2>
      <Calendar
        activeStartDate={date}
        formatDay={() => ""}
        locale="cs"
        showNeighboringMonth={false}
        showNavigation={false}
        tileContent={createCalendarDay}
        tileDisabled={isClickDisabled}
        defaultView="month"
        maxDetail="month"
        minDetail="month"
        view="month"
      />
    </div>
  );
};

export default memo(CalendarMonth);
